import React, {useContext, useEffect} from 'react';
import * as R from 'ramda';
import BootstrapTable from 'react-bootstrap-table-next';
import {useTranslation} from 'react-i18next';
import querystring from 'querystring';
import paginationFactory, {
  PaginationProvider,
  SizePerPageDropdownStandalone,
  PaginationTotalStandalone,
  PaginationListStandalone,
} from 'react-bootstrap-table2-paginator';

import {Row, Col} from 'reactstrap';
import {toDollar} from '@/util';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';

import {SortableLabel} from '@/views/Layout/b2b/SortableList';
import Icon from '@/views/shared/Icon';
import DownloadFileButton from '@/views/shared/DownloadFileButton';

import Pop from '@/b2b/components/Pop';
import styles from '@/b2b/index.module.css';

import PolicyDetail from '@/b2b/pages/NewBusiness/PolicyDetail';

import useAPI from '@/hooks/useAPI';
import {nbPolicyList} from '@/b2b/apis';
import {NewBusinessCtx} from '@/b2b/contexts';
import LoadingOverlay from 'react-loading-overlay';
import {SystemConfigCtx} from '@/contexts';

const PolicyList = () => {
  const [{paging, queryParams}, dispatch] = useContext(NewBusinessCtx);
  const {result, loading, trigger} = useAPI(nbPolicyList, {pending: true});
  const {items = [], totalSize = 0} = R.pathOr({}, ['data'], result);
  const {t: tr} = useTranslation();
  const {siteCode} = useContext(SystemConfigCtx);
  const subtr = tr('nb:table_fields');

  useEffect(
    () => {
      trigger(queryParams);
    },
    [queryParams]
  );

  const columns = [
    {
      dataField: 'PolicyCat',
      text: '',
      formatter: (cell, row) => <SortableLabel shortCode={row.category} />,
      headerStyle: {width: '2%'},
    },
    {
      dataField: 'policyNo',
      text: subtr.policy_no,
      formatter: (cell, row) => (
        <div>
          <span>
            <b>{row.policyNo}</b>
          </span>
          <br />
          <span>{row.productName}</span>
        </div>
      ),
      headerStyle: {width: '18%'},
      sort: true,
    },
    {
      dataField: 'clientName',
      text: subtr.client,
      headerStyle: {width: '12%'},
    },
    {
      dataField: 'insuredName',
      text: subtr.insured,
      headerStyle: {width: '12%'},
    },
    {
      dataField: 'modalPremium',
      text: subtr.modal_premium,
      formatter: (cell, row) =>
        R.join(' ', [row.currency, toDollar(row.modalPremium)]),
      headerStyle: {width: '10%'},
    },
    {
      dataField: 'commenceOn',
      text: subtr.plan_start_date,
      headerStyle: {width: '10%'},
    },
    {
      dataField: 'receivedDate',
      text: subtr.app_rec_date,
      sort: true,
      headerStyle: {width: '12%'},
    },
    ...(siteCode === 'absg'
      ? []
      : [
          {
            dataField: 'stage',
            text: subtr.stage,
            sort: true,
            headerStyle: {width: '8%'},
          },
        ]),
    {
      dataField: 'consultant',
      text: subtr.consultant,
      sort: true,
      headerStyle: {width: '10%'},
    },
    {
      dataField: 'caaId',
      text: subtr.details,
      formatter: (cell, row) => (
        <Pop
          provider={{component: PolicyDetail, caaId: row.caaId}}
          icon='align-justify'
        />
      ),
    },
  ];

  const paginationOpts = {
    custom: true,
    totalSize,
    page: paging.page,
    paginationTotalRenderer: (from, to, size) => (
      <span>{tr('paginate_status', {from, to, size})}</span>
    ),
    sizePerPageList: [10, 20, 30],
  };

  const url = () =>
    `/b2b/nb_export?${querystring.stringify(
      R.omit(['items', 'sizePerPage', 'totalSize'], queryParams)
    )}`;

  const onTableChange = (type, {page, sizePerPage, sortField, sortOrder}) => {
    dispatch({
      type: 'changePaging',
      payload: {page, sizePerPage, sortField, sortOrder},
    });
  };

  return (
    <LoadingOverlay active={loading} spinner text={tr('loading')}>
      <PaginationProvider pagination={paginationFactory(paginationOpts)}>
        {({paginationProps, paginationTableProps}) => (
          <>
            <BootstrapTable
              bootstrap4
              remote
              keyField='caaId'
              data={items}
              columns={columns}
              bordered={false}
              onTableChange={onTableChange}
              {...paginationTableProps}
            />
            <Row>
              <Col md='4'>
                <DownloadFileButton
                  className={`btn btn-outline-primary btn-sm ${styles.btn_sm}`}
                  path={url()}
                >
                  <span>
                    {' '}
                    <Icon icon='file-excel' /> {tr('sr:export_excel')}
                  </span>
                </DownloadFileButton>
                &nbsp;
                <PaginationTotalStandalone {...paginationProps} />
              </Col>
              <Col md='4 text-center'>
                <SizePerPageDropdownStandalone
                  {...paginationProps}
                  btnContextual={`btn btn-primary btn-sm ${styles.btn_sm}`}
                />{' '}
                {tr('per_page')}
              </Col>
              <Col md='4 text-right' className={styles.pagination_list}>
                <PaginationListStandalone {...paginationProps} />
              </Col>
            </Row>
          </>
        )}
      </PaginationProvider>
    </LoadingOverlay>
  );
};

export default PolicyList;
